import { useCallback, useEffect, useMemo, useState } from "react";
import { MDBCol, MDBRow } from "mdbreact";
import * as _ from "lodash";
import FuelRegistryDropdown from "./Fields/FuelRegistryDropdown";
import FuelRegistryInput from "./Fields/FuelRegistryInput";
import { fuelRegistryFieldConfigs, blendFuelRegistryFieldConfigs, NA_FIELDS, FUEL_CATEGORIES, DISABLED_FIELDS, ALWAYS_DISABLED_FIELDS, OVERWRITABLE_FIELDS, EU_FIELD} from "./Fields/FieldConfig";


const NonBlendFuelRegistryForm = ({ filteredBaseFuels, fuelState, onFuelChange, classification, showErrors, isBlendChild, classificationChanged }) => {
  
    const [isInputChanged, setIsInputChanged] = useState({});

    const isFuelCodeInvalid = showErrors && !fuelState.fuelType;
const getInitialConfigs = (classification) => {
    const baseConfig =
        classification === FUEL_CATEGORIES.BLEND
            ? _.cloneDeep(blendFuelRegistryFieldConfigs)
            : _.cloneDeep(fuelRegistryFieldConfigs);
    if (isBlendChild
    ? fuelState.categoryType === FUEL_CATEGORIES.E_FUEL
    : classification === FUEL_CATEGORIES.E_FUEL) {

        // Insert EU field in 3rd row (E row)
        const thirdRow = baseConfig[2];

        const eIndex = thirdRow.findIndex(f => f.metaKey === "E|VALUE|GCO2EQ|MJ");

        if (eIndex !== -1) {
            thirdRow.splice(eIndex + 1, 0, EU_FIELD);
        }
        // Move CSLIP to first row
        const firstRow = baseConfig[0];
         if (firstRow) {
            firstRow.forEach(field => {
                if (field.display) {
                    field.col = 3;
                }
            });
        }

        let cslipField;

        baseConfig.forEach(row => {
            const index = row.findIndex(f => f.metaKey === "CSLIP");
            if (index !== -1) {
                cslipField = row.splice(index, 1)[0];
            }
        });

        if (cslipField) {
            firstRow.push(cslipField);
        }
    }

    return baseConfig;
};

    const [registryFieldConfigs, setRegistryFieldConfigs] = useState(
        getInitialConfigs(classification)
    );

    const getDisplayvalue = (field) => {
        const value = field.isMeta ? getMetaValue(field.metaKey) : getFieldValue(field.fieldName)
        const fieldKey = field.metaKey || field.fieldName;
        const isNAField = NA_FIELDS[classification]?.includes(fieldKey);

        if (isNAField && !value)
            return "Not Applicable";

        return value;
    }
const isFieldInvalid = (field) => {
	if (field.showNA) return false;

	const fieldKey = field.metaKey || field.fieldName;
	const value = field.isMeta ? getMetaValue(field.metaKey) : getFieldValue(field.fieldName);
	const isEFuel = isBlendChild ? fuelState.categoryType === FUEL_CATEGORIES.E_FUEL : classification === FUEL_CATEGORIES.E_FUEL;

	
	if (isEFuel && fieldKey === "EU|VALUE") {
		
		if (showErrors) {
			return !value || value.toString().trim() === "";
		}
		return false;
	}

	return showErrors && isFieldRequired(fieldKey) && (!value || value.toString().trim() === "");
};

    const updateMultipleConfigFields = predicate => updates => {
        setRegistryFieldConfigs(prev =>
            prev.map(row =>
                row.map(field =>
                    predicate(field) ? { ...field, ...updates } : field
                )
            )
        );
    };

    const handleChange = (value, dataKey) => {
        onFuelChange((prev) => ({
            ...prev,
            [dataKey]: value,
        }));
    };
    useEffect(() => {
        setRegistryFieldConfigs(getInitialConfigs(classification));
    }, [classification]);

   useEffect(() => {
		updateMultipleConfigFields(() => true)({ disabled: true });

		const isEdit = fuelState.fuelRegId > 0;

		const enableFields = ["BDN|ID", "POS|ID", "QUANTITY|MT"];

		if (!isEdit && classificationChanged) {
			onFuelChange((prev) => ({
				...prev,
				metadata: [],
			}));
		}

		if (fuelState.fuelType) {
			updateMultipleConfigFields((x) => enableFields.includes(x.fieldName))({ disabled: false });

			if (!isEdit) {
				const baseFuelData = [...filteredBaseFuels].reverse().find((x) => x.fuelType === fuelState.fuelType);

				if (baseFuelData) {
					onFuelChange((prev) => ({
						...prev,
						fuelName: baseFuelData.fuelName ?? "",
						metadata:
							baseFuelData.metadata?.map((m) => ({
								...m,
								isOverWritten: false,
							})) ?? [],
					}));
				}
			}
		}
   }, [fuelState.fuelType, fuelState.fuelRegId, classificationChanged]);




    const normalize = (v) => {
        if (v === null || v === undefined || v === "") return null;
        return String(v).trim();
    };

    const handleMetaChange = (value, metaKey, metaType) => {
        setIsInputChanged(prevVal => {
            return { ...prevVal, [metaKey]: true };
        })
        const baseValue = normalize(getBaseMetaValue(metaKey));
        const currentValue = normalize(value);

        const OVERWRITABLE_SET = new Set(OVERWRITABLE_FIELDS);
       
       let isOverWritable;
		const fuelCode = fuelState.fuelType?.split(".")[0];
        const isImoLcv = metaKey === "IMO|LCV";
        const isEuLcv = metaKey === "EU|LCV";
	    const isBiofuel = isBlendChild
        ? fuelState.categoryType === FUEL_CATEGORIES.BIOFUEL
        : classification === FUEL_CATEGORIES.BIOFUEL;

        const isEfuels = isBlendChild
        ? fuelState.categoryType === FUEL_CATEGORIES.E_FUEL
        : classification === FUEL_CATEGORIES.E_FUEL;
        
        const isImoCfCo2 = metaKey === "IMO|CF|CO2";

    if (isImoLcv) {
		isOverWritable = (isBiofuel && fuelCode !== "ETHA") || isEfuels;
	} else if (isEuLcv) {
		isOverWritable = isEfuels;
	} else if (isImoCfCo2) {
		isOverWritable = isBiofuel && fuelCode !== "ETHA";
	} else {
		isOverWritable = OVERWRITABLE_SET.has(metaKey);
	}

	const previousMeta = fuelState.metadata?.find((m) => m.metaKey === metaKey);

	const isOverWritten = isOverWritable && (previousMeta?.isOverWritten || (currentValue !== null && currentValue !== baseValue));

        onFuelChange(prev => {
            const metadata = prev.metadata ?? [];
            const metaIndex = metadata.findIndex(m => m.metaKey === metaKey);

            const updatedMeta = {
                metaKey,
                metaValue: value,
                metaType,
                isOverWritten,
            };

            const updatedMetadata =
                metaIndex >= 0
                    ? metadata.map((m, i) => (i === metaIndex ? updatedMeta : m))
                    : [...metadata, updatedMeta];
             // Reset override of IMO|CF|CO2 when inputs affecting it change
            if (
                ["E|VALUE|GCO2EQ|MJ", "IMO|LCV","ENERGY|CONTENT|MJ","QUANTITY|MT"].includes(metaKey)
            ) {
                const imoCfIndex = updatedMetadata.findIndex(
                    m => m.metaKey === "IMO|CF|CO2"
                );

                if (imoCfIndex >= 0) {
                    updatedMetadata[imoCfIndex] = {
                        ...updatedMetadata[imoCfIndex],
                        isOverWritten: false,
                    };
                }
            }

            // Reset dependent calculated fields
            if (["QUANTITY|MT", "ENERGY|CONTENT|MJ"].includes(metaKey)) {
                ["IMO|LCV"].forEach(key => {
                    const idx = updatedMetadata.findIndex(m => m.metaKey === key);

                    if (idx >= 0) {
                        updatedMetadata[idx] = {
                            ...updatedMetadata[idx],
                            isOverWritten: false,
                        };
                    }
                });
            }

            if (["E|VALUE|GCO2EQ|MJ", "IMO|LCV"].includes(metaKey)) {
                ["IMO|CF|CO2"].forEach(key => {
                    const idx = updatedMetadata.findIndex(m => m.metaKey === key);

                    if (idx >= 0) {
                        updatedMetadata[idx] = {
                            ...updatedMetadata[idx],
                            isOverWritten: false,
                        };
                    }
                });
            }
           
            return { ...prev, metadata: updatedMetadata };
        });
    };

    const getBaseFuelType = () => {
        if (!fuelState.fuelType) return "";
        return fuelState.fuelType.split(".")[0]; // "EMETH"
    };

    const getBaseMetaValue = (metaKey) => {

        if (!OVERWRITABLE_FIELDS.includes(metaKey)) return undefined;
        if (!fuelState.fuelType) return "";
           if (metaKey === "IMO|LCV") {

            

        const energy = parseFloat(getMetaValue("ENERGY|CONTENT|MJ"));
        const quantity = parseFloat(getMetaValue("QUANTITY|MT"));

       if (energy && quantity) {
    return (energy / (quantity * 1_000_000)).toString();
    }


        const baseFuelType = getBaseFuelType();
        const baseFuel = filteredBaseFuels.find(
        f => f.fuelType === baseFuelType
        );

return (
    baseFuel?.metadata?.find(m => m.metaKey === metaKey)?.metaValue ?? ""
);

        return (energy / (quantity * 1_000_000)).toString();
       
       }
        const baseFuelType = getBaseFuelType();
        const baseFuel = filteredBaseFuels.find(
            f => f.fuelType === baseFuelType
        );

        return (
            baseFuel?.metadata?.find(m => m.metaKey === metaKey)?.metaValue ?? ""
        );
    };

    const restoreMetaValue = (metaKey) => {
        const baseValue = getBaseMetaValue(metaKey);
        onFuelChange(prev => ({
            ...prev,
            metadata: prev.metadata.map(m =>
                m.metaKey === metaKey
                    ? { ...m, metaValue: baseValue, isOverWritten: false }
                    : m
            )
        }));
        setIsInputChanged(prevVal => {
            return { ...prevVal, [metaKey]: false };
        })
    };


    const isFieldRequired = (fieldKey) => {
        if (fieldKey === "fuelType" || fieldKey === "code") return false;
        if (isBlendChild &&
            fuelState.categoryType === FUEL_CATEGORIES.E_FUEL &&
            ["ENERGY|CONTENT|MJ"].includes(fieldKey)
        ) {
            return false;
        }
        if (isBlendChild &&
            fuelState.categoryType === FUEL_CATEGORIES.FOSSIL &&
            ["ENERGY|CONTENT|MJ", "E|VALUE|GCO2EQ|MJ"].includes(fieldKey)
        ) {
            return false;
        }

        if (
            classification === FUEL_CATEGORIES.E_FUEL &&
            ["QUANTITY|MT", "ENERGY|CONTENT|MJ"].includes(fieldKey)
        ) {
            return false;
        }
        if (
            classification === FUEL_CATEGORIES.FOSSIL &&
            ["QUANTITY|MT", "ENERGY|CONTENT|MJ", "E|VALUE|GCO2EQ|MJ"].includes(fieldKey)
        ) {
            return false;
        }

        if (classification === FUEL_CATEGORIES.BIOFUEL &&
            ["QUANTITY|MT", "ENERGY|CONTENT|MJ", "E|VALUE|GCO2EQ|MJ"].includes(fieldKey)) {
            return true
        }

        return _.chain(registryFieldConfigs)
            .flatten()
            .filter((x) => x.required)
            .map((x) => x.metaKey || x.fieldName)
            .includes(fieldKey)
            .value();
    };

    const isFieldDisabled = (fieldKey) => {

        if (classification === FUEL_CATEGORIES.BLEND && fuelState.fuelRegId > 0 && ["fuelName"].includes(fieldKey)) {
            return false;
        } else {
            if (classification !== FUEL_CATEGORIES.BLEND && ["fuelName"].includes(fieldKey)) {
                return true;
            }
        }
        if (fieldKey === "IMO|LCV") {
			const fuelCode = fuelState.fuelType?.split(".")[0];
			if (fuelCode === "ETHA") return true;
			if (classification === FUEL_CATEGORIES.BIOFUEL) return false;
		}

        if ((isBlendChild && fuelState.categoryType === FUEL_CATEGORIES.OTHER) || classification === FUEL_CATEGORIES.OTHER) return false;

        if(classification!==FUEL_CATEGORIES.FOSSIL){
            if(fieldKey=="E|VALUE|GCO2EQ|MJ"){
                return false;
            }
        }

        if(fieldKey === "IMO|CF|CO2"){
            const fuelCode = fuelState.fuelType?.split(".")[0];
            if(fuelCode === "ETHA") return true;
            if(classification === FUEL_CATEGORIES.BIOFUEL) return false;
        }

        if (ALWAYS_DISABLED_FIELDS.includes(fieldKey)) return true;

        if (DISABLED_FIELDS[classification]?.includes(fieldKey)) return true;

        const metaType = metaTypeMap[fieldKey];

        if (metaType === undefined || metaType === null)
            return false;

        if (fieldKey)
            return metaType !== 1;
    };  


    const getMetaValue = (metaKey) => {
        let metaData = _.find(fuelState.metadata, (x) => x.metaKey === metaKey);
        return metaData ? metaData.metaValue : "";
    };

    const getFieldValue = (fieldName) => {
        return fuelState[fieldName] || "";
    };

    const metaTypeMap = useMemo(() => {
        return fuelState.metadata?.reduce((acc, m) => {
            acc[m.metaKey] = m.metaType;
            return acc;
        }, {}) || {};
    }, [fuelState.metadata]);

    const isOverWritten = (metaKey) => {
		if (classification === FUEL_CATEGORIES.BLEND && fuelState.categoryType !== FUEL_CATEGORIES.E_FUEL) return false;
		if (!fuelState.fuelType) return false;
		if (!OVERWRITABLE_FIELDS.includes(metaKey)) return false;


    const isEfuels = isBlendChild
        ? fuelState.categoryType === FUEL_CATEGORIES.E_FUEL
        : classification === FUEL_CATEGORIES.E_FUEL;
    const isEdit = fuelState.fuelRegId > 0;
    if (isEfuels && !isEdit && !isInputChanged[metaKey]) return false;
		if (classificationChanged && !isInputChanged[metaKey]) return false;
		if (metaKey === "IMO|LCV") {
			const fuelCode = fuelState.fuelType?.split(".")[0];
			if (fuelCode === "ETHA") return false;
			if (
				isBlendChild &&
				(fuelState.categoryType === FUEL_CATEGORIES.OTHER ||
					fuelState.categoryType === FUEL_CATEGORIES.FOSSIL)
			)
				return false;
			if (classification === FUEL_CATEGORIES.OTHER || classification === FUEL_CATEGORIES.FOSSIL)
				return false;
		}
         if (metaKey === "EU|LCV") {
        const isEfuels = isBlendChild
        ? fuelState.categoryType === FUEL_CATEGORIES.E_FUEL
        : classification === FUEL_CATEGORIES.E_FUEL;

        if (!isEfuels) return false;
    }
     if (metaKey === "IMO|CF|CO2") {
        const meta = fuelState.metadata?.find(
            x => x.metaKey === "IMO|CF|CO2"
        );

        return !!meta?.isOverWritten;
    }
		const baseValue = getBaseMetaValue(metaKey) ?? "";
		const baseNum = parseFloat(baseValue);
		if (baseValue === undefined) return false;
		const currentValue = getMetaValue(metaKey) ?? "";
		const currentNum = parseFloat(currentValue);

		return currentNum !== baseNum;
	};

    return (
        <div style={{ marginTop: "1rem" }}>
            {/* Fuel Code Dropdown*/}
            {classification !== FUEL_CATEGORIES.BLEND && (
                <MDBRow className="mb-4" noGutters>
                    <MDBCol md="3">
                        {fuelState.fuelRegId ? (
                            <FuelRegistryInput
                                label="Fuel Code"
                                name="fuelType"
                                disabled={true}
                                value={fuelState.fuelType}
                            />
                        ) : (
                            <>
                                <FuelRegistryDropdown
                                    key={`${fuelState.fuelRegId}-${classificationChanged}`}
                                    label="Fuel Code"
                                    name="fuelType"
                                    required={true}
                                    selected={fuelState.fuelType}
                                    options={filteredBaseFuels}
                                    handleChange={(e) => {
                                        const newFuelType = e.value;
                                        onFuelChange((prev) => ({
                                            ...prev,
                                            fuelType: newFuelType,
                                            fuelName: "",
                                            mass: null,
                                            metadata: [],
                                        }));
                                    }}

                                />

                                {isFuelCodeInvalid && (
                                    <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                        Fuel Code is required
                                    </div>
                                )}
                            </>
                        )}
                    </MDBCol>
                </MDBRow>
            )}
            {/* Render all form fields from configuration */}
            {registryFieldConfigs.map((row, rowIndex) => (
                <MDBRow key={`row-${rowIndex}`} className="mb-4" noGutters>
                    {row
                        .filter((x) => x.display)
                        .map((field, fieldIndex) => {
                            const fieldKey = field.metaKey || field.fieldName;
                            const fieldName = field.metaKey || field.fieldName;

                            return (
                                <MDBCol key={`col-${rowIndex}-${fieldIndex}`} md={field.col}>
                                    {field.isDropdown ? (
                                        <FuelRegistryDropdown
                                            label={field.label}
                                            name={fieldName}
                                            options={field.options}
                                            required={isFieldRequired(fieldKey)}
                                            disabled={isFieldDisabled(fieldKey)}
                                            includeSelectOption={field.includeSelectOption}
                                            selected={field.isMeta ? getMetaValue(field.metaKey) : getFieldValue(field.fieldName)}
                                            metaKey={field.isMeta ? field.metaKey : undefined}
                                            onChange={!field.isMeta ? (e) => handleChange(e, field.fieldName) : undefined}
                                            handleMetaChange={field.isMeta ? handleMetaChange : undefined}
                                            handleChange={field.isMeta ? (val) => handleMetaChange(val.value, field.metaKey, 1) : undefined}
                                            categoryType={fuelState.categoryType}
                                        />
                                    ) : (
                                        <FuelRegistryInput
                                            label={field.label}
                                            name={fieldName}
                                            required={field.required ? isFieldRequired(fieldKey) : false}
                                            disabled={field.showNA || isFieldDisabled(fieldKey)}
                                            value={getDisplayvalue(field)}
                                            metaKey={field.isMeta ? field.metaKey : undefined}
                                            onChange={!field.isMeta ? (e) => handleChange(e, field.fieldName) : undefined}
                                            handleMetaChange={field.isMeta ? handleMetaChange : undefined}
                                            style={{
                                                textAlign: field.alignRight ? "right" : "left",
                                            }}
                                            error={isFieldInvalid(field)}
                                            showNA={field.showNA}
                                            isOverWritten={isOverWritten(fieldKey)}
                                            onRestore={() => restoreMetaValue(field.metaKey)}
                                            categoryType={fuelState.categoryType}

                                        />
                                    )}
                                </MDBCol>
                            );
                        })}
                </MDBRow>
            ))}
        </div>
    );
};

export default NonBlendFuelRegistryForm;